import React, { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import FieldLabel from 'components/shared/form/FieldLabel';
import { colors } from 'styles/theme/colors';

type Props = {
  title: string;
  placeholder: string;
  value: string;
  readOnly: boolean;
  onApply: (value: string) => void;
  onClose: () => void;
};

export default function ConfigFieldPanel({ title, placeholder, value, readOnly, onApply, onClose }: Props) {
  const [localValue, setLocalValue] = useState(value);

  const handleApply = () => {
    onApply(localValue);
    onClose();
  };

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
        <CloseButton onClick={onClose} />
      </PanelHeader>
      <PanelContent>
        <FieldLabel name={title}>{title}</FieldLabel>
        <PanelTextarea
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={placeholder}
          disabled={readOnly}
        />
      </PanelContent>
      <PanelFooter>
        <ButtonGroup>
          <Button variant='ghost' onClick={onClose}>
            {readOnly ? 'Go Back' : 'Cancel'}
          </Button>
          {!readOnly && <Button onClick={handleApply}>Apply</Button>}
        </ButtonGroup>
      </PanelFooter>
    </PanelContainer>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const PanelTextarea = styled.textarea`
  width: 100%;
  flex: 1;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  padding: 12px;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  resize: none;
  min-height: 300px;

  &::placeholder {
    color: ${colors.black25};
  }

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;
